import {
  useEffect,
  forwardRef,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import TextInputInline from 'components/Inputs/inline/TextInput';
import { getDataQAForInput } from 'components/Inputs/helpers';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import usePhoneFormatter from 'hooks/usePhoneFormatter';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { useTranslation } from 'react-i18next';
import { useFlashTransition } from 'hooks/useFlashState';
import { isCountryCallingCode } from '../helpers';
import PhoneNumberWithOverlay from './PhoneNumberWithOverlay';
import EditableText from 'components/Kizen/EditableText';
import styled from '@emotion/styled';
import { gutters } from 'app/spacing';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { toastVariant, useToast } from 'components/ToastProvider';
import { useRecordDetailCustomScript } from '@kizenapps/engine/react';
import { WithIconDataAdornments } from 'components/Inputs/Adornments/WithIconDataAdornments';
import { ADORNMENT_VARIANTS } from 'components/Inputs/Adornments/constants';

const getInputEl = (inputRef) => {
  // Get ref to input, handling case of react-input-autosize's getInput()
  return inputRef.current && inputRef.current.getInput
    ? inputRef.current.getInput()
    : inputRef.current;
};

const PhoneNumberInline = styled.div`
  .ViewText {
    margin-top: -${gutters.spacing(1, -4)}px;
    max-width: 100%;
  }
  .ViewText input:not(:hover) {
    background-image: none;
  }
  input {
    min-width: 30px;
    padding-left: 0;
  }
  &&& input[disabled] {
    background: unset;
    // A bit blunt, but we need to make sure there's no image otherwise the background repeats
    background-image: none !important;
    color: var(--color-font-placeholder);
  }
`;

const emptyValue = '—';

const PhoneNumberInlineField = forwardRef(
  (
    {
      field,
      objectId,
      entityId,
      input,
      value,
      onSubmit,
      enableExtension,
      editable,
      callback,
      submitUnchanged,
      autoFocus,
      onAutoFocus,
      readOnly,
      onKeyPress,
      emptyPlaceholder = emptyValue,
      defaultCountry = 'US',
      disabled,
      'data-field-type': qaFieldType,
      'data-field-id': qaFieldId,
      shouldFocusNext,
      keyListenerId,
      adornmentObjectId,
      adornmentEntityId,
      adornments = [],
      ...others
    },
    ref
  ) => {
    const { assignFieldHandle, getKeyListenersProps } = useKeyBoardContext();
    const [editing, setEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [stagedValue, setStagedValue] = useState(value);
    const [isError, setIsError] = useState(false);
    const [error, showErrorMessage, flashError, cancelFlash] =
      useFlashTransition();
    const inputRef = useRef();
    const targetRef = useRef(null);
    const { t } = useTranslation();

    const onAutoFocusRef = useRef();
    onAutoFocusRef.current = onAutoFocus;

    useEffect(() => {
      const inputEl = getInputEl(inputRef); // || getInputEl(validationRef);
      if (autoFocus && inputEl && !getKeyListenersProps) {
        if (onAutoFocusRef.current) onAutoFocusRef.current(targetRef);
        inputEl.focus({ preventScroll: true });
      }
    }, [autoFocus, getKeyListenersProps]);

    const { formattedValue } = usePhoneFormatter(value, defaultCountry);

    const handleChange = useCallback(
      (v) => {
        setStagedValue(v);

        if (callback) {
          callback();
        }
      },
      [callback]
    );

    useEffect(() => {
      setStagedValue(value);
    }, [value, editing]);

    const submit = useCallback(
      async ({ clickAway = false } = {}, data) => {
        const phoneData = data ?? stagedValue;
        if (submitUnchanged || value !== phoneData) {
          try {
            setSubmitting(true);
            if (
              phoneData &&
              !isValidPhoneNumber(phoneData) &&
              !isCountryCallingCode(phoneData)
            ) {
              setIsError(true);
              const errMsg = t('Please enter a valid phone number');
              flashError(errMsg);
              if (getKeyListenersProps && !clickAway) {
                return true;
              }
              throw new Error(errMsg);
            }
            await onSubmit(phoneData);
            setEditing(false);
            setIsError(false);

            if (clickAway && inputRef?.current) {
              inputRef?.current.blur();
            }
            if (getKeyListenersProps && !clickAway) {
              return false;
            }
          } catch (err) {
            if (isCountryCallingCode(phoneData)) {
              setIsError(false);
              setEditing(false);
            }
            if (err instanceof TextInputInline.Error && !err.failed) {
              if (getKeyListenersProps && !clickAway) {
                return false;
              }
              return;
            }
            if (clickAway && isError) {
              // When clicking away after two failures, bail on editing
              setIsError(false);
              setEditing(false);
              cancelFlash();
              if (getKeyListenersProps && !clickAway) {
                return false;
              }
            }
          } finally {
            setSubmitting(false);
          }
        } else {
          setEditing(false);
          cancelFlash();
          if (getKeyListenersProps && !clickAway) {
            return false;
          }
        }
      },
      [
        getKeyListenersProps,
        flashError,
        onSubmit,
        stagedValue,
        submitUnchanged,
        t,
        value,
        isError,
        cancelFlash,
      ]
    );

    const handleFocus = useCallback((ev) => {
      setEditing(true);
      ev.target.click();
    }, []);

    const fieldId = useMemo(
      () => keyListenerId || `${field?.id}-${entityId}`,
      [keyListenerId, field, entityId]
    );

    const [showToast] = useToast();

    const [executeScript, internalState] = useRecordDetailCustomScript({
      entityId,
      objectId,
      onError: (e) => {
        showToast({
          message: `${t('Action could not run')}: ${e?.message}`,
          variant: toastVariant.FAILURE,
        });
      },
    });

    assignFieldHandle(fieldId, {
      customFocus: () => {
        setEditing(true);
        return targetRef.current;
      },
      disabled: readOnly,
      shouldFocusNext: shouldFocusNext,
    });

    const phoneNumberAdornments = adornments.filter(
      (adornment) => adornment.field_type === 'phonenumber'
    );

    const hasAdornments = phoneNumberAdornments.length > 0 && formattedValue;

    const readyOnlyView = readOnly ? (
      <WithIconDataAdornments
        hasAdornments={hasAdornments}
        adornments={phoneNumberAdornments}
        scriptArgs={{
          fieldId: field?.id,
          fieldType: field?.fieldType,
          objectId: adornmentObjectId ?? objectId,
          entityId: adornmentEntityId ?? entityId,
          value,
        }}
        disabled={internalState.pending}
        executeScript={executeScript}
        variant={ADORNMENT_VARIANTS.inline}
      >
        <TextEllipsisWithTooltip {...others}>
          {formattedValue}
        </TextEllipsisWithTooltip>
      </WithIconDataAdornments>
    ) : null;

    const editingView = editing ? (
      <>
        <EditableText
          className="ViewText"
          value={stagedValue ?? emptyPlaceholder}
          disabled={submitting || editing || readOnly}
          onClick={() => {
            setEditing(true);
          }}
        />
        <PhoneNumberWithOverlay
          editing={editing}
          submitting={submitting}
          targetRef={targetRef}
          ref={ref}
          others={others}
          showErrorMessage={showErrorMessage}
          error={error}
          stagedValue={stagedValue}
          inputRef={inputRef}
          handleChange={handleChange}
          enableExtension={enableExtension}
          defaultCountry={defaultCountry}
          qaFieldType={qaFieldType}
          qaFieldId={qaFieldId}
          submit={submit}
          field={field}
          entityId={entityId}
          t={t}
          keyListenerId={keyListenerId}
          {...getKeyListenersProps(fieldId)}
          onBlur={() => {
            setEditing(false);
          }}
        />
      </>
    ) : (
      <WithIconDataAdornments
        hasAdornments={hasAdornments}
        adornments={phoneNumberAdornments}
        scriptArgs={{
          objectId,
          fieldId: field?.id,
          fieldType: field?.fieldType,
          value,
        }}
        disabled={internalState.pending}
        executeScript={executeScript}
        variant={ADORNMENT_VARIANTS.inline}
      >
        <TextInputInline
          field={field}
          isShowCheckIcon={false}
          ref={inputRef}
          inputRef={(el) => (inputRef.current = el)}
          value={
            (submitting ? stagedValue : formattedValue) ?? emptyPlaceholder
          }
          editing
          autoFocus={autoFocus}
          onFocus={handleFocus}
          disabled={disabled || submitting}
          {...getDataQAForInput(qaFieldId, qaFieldType)}
        />
      </WithIconDataAdornments>
    );

    return (
      <PhoneNumberInline ref={targetRef}>
        {readOnly ? readyOnlyView : editingView}
      </PhoneNumberInline>
    );
  }
);

export default PhoneNumberInlineField;
